/**
 * Strata family renderer — sand grain geological simulation.
 * Ported faithfully from meridian-generator-final.html reference.
 *
 * Signature: render(ctx, W, H, palette, params, rand)
 *
 * This renderer simulates sand grains falling from a nozzle and piling up,
 * creating geological strata layers. Each layer has a different color.
 * Wind affects horizontal drift, repose angle controls slope steepness.
 *
 * NOTE: This runs on the main thread in Phase A. Web Worker deferred to Phase D.
 */

// ═══════════════════════════════════════════════════════════
//  STRATA — sand grain geological simulation
// ═══════════════════════════════════════════════════════════

export function renderStrata(ctx, W, H, pal, P, rand) {
  const {
    nozzleWidth = 4,
    layers = 14,
    windStrength = 30,
    reposeAngle = 3,
  } = P;

  const colors = pal.colors;
  const bgColor = pal.bg;

  // Scale to canvas dimensions
  const scaleX = W / 400;
  const scaleY = H / 540;
  const simW = Math.ceil(W / scaleX);
  const simH = Math.ceil(H / scaleY);

  // Height map — column heights
  const ground = new Float32Array(simW).fill(simH);

  // Color map — which color occupies each pixel
  const colorMap = new Uint8Array(simW * simH);

  // Background
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, W, H);

  // Simulation
  const grainsPerLayer = Math.floor(simW * simH * 0.08);
  const nozzleW = nozzleWidth;
  const repose = reposeAngle;

  for (let layer = 0; layer < layers; layer++) {
    const colorIdx = layer % colors.length;
    const windAngle = (rand() - 0.5) * 2;
    const wind = windAngle * windStrength * 0.01;

    // Nozzle sweeps across the width
    for (let g = 0; g < grainsPerLayer; g++) {
      // Nozzle position oscillates
      const t = g / grainsPerLayer;
      const nozzleCenter = simW * t;
      const x = Math.round(nozzleCenter + (rand() - 0.5) * nozzleW * 2);
      if (x < 0 || x >= simW) continue;

      // Drop grain
      let col = x + Math.round(wind * (simH - ground[x]) * 0.05);
      if (col < 0 || col >= simW) continue;

      // Find landing height
      let landY = Math.floor(ground[col]) - 1;
      if (landY < 0) continue;

      // Apply repose angle (slide to neighbors if too steep)
      let settled = false;
      for (let attempt = 0; attempt < 12; attempt++) {
        if (landY < 0 || landY >= simH) break;

        const leftH = col > 0 ? ground[col - 1] : simH;
        const rightH = col < simW - 1 ? ground[col + 1] : simH;
        const curH = ground[col];

        if (curH - leftH > repose && leftH <= rightH) {
          col--;
        } else if (curH - rightH > repose && rightH < leftH) {
          col++;
        } else {
          settled = true;
          break;
        }

        if (col < 0 || col >= simW) break;
        landY = Math.floor(ground[col]) - 1;
      }

      if (!settled || col < 0 || col >= simW || landY < 0 || landY >= simH) continue;

      // Place grain
      ground[col] = Math.min(ground[col], landY);
      colorMap[landY * simW + col] = colorIdx + 1; // +1 so 0 = empty
    }
  }

  // Render to canvas using ImageData for performance
  const img = ctx.createImageData(W, H);
  const data = img.data;

  // Parse bg color
  const bgMatch = bgColor.match(/\d+/g);
  const bgR = bgMatch ? parseInt(bgMatch[0]) : 240;
  const bgG = bgMatch ? parseInt(bgMatch[1]) : 236;
  const bgB = bgMatch ? parseInt(bgMatch[2]) : 228;

  // Parse all palette colors to RGB
  const parsedColors = colors.map(c => {
    if (c.startsWith('rgb')) {
      const m = c.match(/\d+/g);
      return [parseInt(m[0]), parseInt(m[1]), parseInt(m[2])];
    }
    return [
      parseInt(c.slice(1, 3), 16),
      parseInt(c.slice(3, 5), 16),
      parseInt(c.slice(5, 7), 16),
    ];
  });

  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      const sx = Math.floor(px / scaleX);
      const sy = Math.floor(py / scaleY);
      const idx = (py * W + px) * 4;

      if (sx >= 0 && sx < simW && sy >= 0 && sy < simH) {
        const cIdx = colorMap[sy * simW + sx];
        if (cIdx > 0 && cIdx <= parsedColors.length) {
          const [r, g, b] = parsedColors[cIdx - 1];
          data[idx] = r; data[idx + 1] = g; data[idx + 2] = b; data[idx + 3] = 255;
          continue;
        }
      }

      data[idx] = bgR; data[idx + 1] = bgG; data[idx + 2] = bgB; data[idx + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
}
